import { PermissionAction, PermissionModule, parsePermission } from "@ibada/shared-types/authorization"

import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import {
  BranchAdministration,
  BranchesManagement,
  RolesManagement,
  SubscriptionManagement,
  WorkspaceSettingsManagement,
  type AppSubject,
} from "@/features/auth/casl/types"

/**
 * Client mirror of the server permission -> CASL mapping. The tenant UI ability is coarse (read / update per
 * module subject), so each `<module>.<action>` permission a user holds via a custom role maps to a module
 * subject + read/update. view => read; edit/approve/manage => read + update. Keep in sync with the server
 * permission-casl-map (the server is authoritative; this only gates the UI).
 */

type ClientSubject = AppSubject
type ClientAction = "read" | "update"

const MODULE_TO_SUBJECT: Record<PermissionModule, ClientSubject> = {
  [PermissionModule.Members]: WorkspaceAccessModule.MEMBERS,
  [PermissionModule.Activities]: WorkspaceAccessModule.ACTIVITIES,
  [PermissionModule.Assets]: WorkspaceAccessModule.ASSETS,
  [PermissionModule.Forms]: WorkspaceAccessModule.FORMS,
  [PermissionModule.Groups]: WorkspaceAccessModule.GROUPS,
  [PermissionModule.Ministries]: WorkspaceAccessModule.MINISTRIES,
  [PermissionModule.Departments]: WorkspaceAccessModule.DEPARTMENTS,
  [PermissionModule.Reports]: WorkspaceAccessModule.REPORTS,
  [PermissionModule.Settings]: WorkspaceAccessModule.SETTINGS,
  [PermissionModule.WorkspaceSettings]: WorkspaceSettingsManagement,
  // User management is the BranchAdministration subject on the client.
  [PermissionModule.Users]: BranchAdministration,
  [PermissionModule.Finance]: WorkspaceAccessModule.FINANCE,
  [PermissionModule.Budgets]: WorkspaceAccessModule.BUDGETING,
  // Organization governance modules (workspace-scoped only).
  [PermissionModule.Branches]: BranchesManagement,
  [PermissionModule.Roles]: RolesManagement,
  [PermissionModule.Subscription]: SubscriptionManagement,
}

/** Map a permission string to the client subject + actions it grants (null for an unknown permission). */
export function clientPermissionGrant(
  permission: string,
): { subject: ClientSubject; actions: ClientAction[] } | null {
  const parsed = parsePermission(permission)
  if (!parsed) return null
  const subject = MODULE_TO_SUBJECT[parsed.module]
  const actions: ClientAction[] = parsed.action === PermissionAction.View ? ["read"] : ["read", "update"]
  return { subject, actions }
}
